import { __rest } from "tslib";
import { ProductTypes, BasketButtonError, BasketResponseCode, BasketStatus, BasketUpdateStatus, PDPVersion, } from '@as-react/common-types';
import { EventEnum } from '@as-react/common-types';
import { findColor, findSize } from '@as-react/common-utils';
import { EventUtil } from '@as-react/common-utils';
import { all, call, fork, getContext, put, select, spawn, takeEvery, takeLatest, throttle, delay, } from 'redux-saga/effects';
import BasketAPI from '../../../api/basket/BasketAPI';
import ProductDetailAPI from '../../../api/ProductDetailAPI';
import { DELAY } from '../../../constants/GeneralConstants';
import { BasketActionTypes, GeneralActionTypes, ProductAddToBasketLocations } from '../../../enums/ActionEnums';
import { makeGetBundleParentProduct, makeGetProductFulfillmentBySize, } from '../../../redux/product/selectors/ProductSelector';
import BasketUtil from '../../../util/BasketUtil';
import { calculateDifferenceInMs } from '../../../util/DateUtil';
import MonetateUtil from '../../../util/MonetateUtil';
import { getDefaultSize, getSizeDescription } from '../../../util/ProductUtil';
import { emitter, EmitterEvent } from '../../../util/tracking';
import { buildCartUpdatePayload } from '../../../util/TrackingUtil';
import { analyticsActions } from '../../analytics/actions/AnalyticsActions.creators';
import { basketActions } from '../actions/BasketActions.creators';
import { makeGetBasketId, makeGetBasketMappedProductsState, makeGetBasketPopupState, makeGetBasketPricingState, makeGetBasketTotalQuantityState, } from '../selectors/BasketSelector';
import { basketOverviewAnalyticsSaga } from './BasketOverviewAnalyticsSaga';
import { cartUpdateTrackingSaga } from './CartUpdateTrackingSaga';
import { changeSizeSaga } from './ChangeSizeSaga';
import { checkStockReservation, checkStockReservationSaga } from './CheckStockReservationSaga';
import { decreaseProductQuantitySaga } from './DecreaseProductQuantitySaga';
import { increaseProductQuantitySaga } from './IncreaseProductQuantitySaga';
import { mergeBasketsSaga } from './MergeBasketsSaga';
import { removeProductAndUpdateSaga } from './RemoveProductAndUpdateSaga';
import { removeProductFromBasketAnalyticsSaga } from './RemoveProductFromBasketAnalyticsSaga';
import { removeProductFromBasketSaga } from './RemoveProductFromBasketSaga';
import { resetBasketSaga } from './ResetBasketSaga';
import { updateBasketProductAnalyticsSaga } from './UpdateBasketProductAnalyticsSaga';
import { updateProductQuantitySaga } from './UpdateProductQuantitySaga';
export const getAddToBasketData = (state) => ({
    colorId: state.product.selectedColorId,
    sku: state.product.selectedSku,
    productCode: state.product.productCode,
    productId: state.product.productId,
});
export const getAddToBasketVoucherData = (state) => {
    var _a;
    return ({
        productCode: state.product.productCode,
        productId: state.product.productId,
        colorId: state.product.defaultColor,
        productColorVariations: state.product.productColorVariations,
        title: state.product.productTranslatedProperties.title,
        brand: (_a = state.product.brand) === null || _a === void 0 ? void 0 : _a.description,
    });
};
export const getSelectedSku = (state) => ({
    sku: state.product.selectedSku,
});
export const getProductData = (state) => {
    var _a;
    const productColor = state.product.productColorVariations.find((productColorVariation) => productColorVariation.colorId === state.product.selectedColorId);
    const productSize = productColor.sizes.find((size) => size.sku === state.product.selectedSku);
    const sizeDescription = getSizeDescription(productSize);
    return {
        sizeDescription,
        description: state.product.productTranslatedProperties.title,
        image: productColor.images[0].picture,
        productCode: state.product.productCode,
        colorDescription: productColor.translations.description,
        swatch: productColor.swatch,
        brand: (_a = state.product.brand) === null || _a === void 0 ? void 0 : _a.description,
        productHasDiscount: productColor.hasDiscount,
        productDiscountAmount: productSize.productDiscountAmount,
        productDiscountPercentage: productSize.productDiscountPercentage,
        prices: productSize.prices,
        productId: state.product.productId,
    };
};
const getCurrentProducts = (state) => state.basket.products;
const getCurrentMappedProducts = (state) => state.basket.mappedProducts;
const validateBasketData = (data, location, isCustomSizeSelector) => {
    if (data.sku === null || data.sku === undefined) {
        if (location === ProductAddToBasketLocations.STICKY_BAR) {
            return BasketButtonError.SKU_STICKY_BAR;
        }
        else if (location === ProductAddToBasketLocations.PDP && isCustomSizeSelector) {
            return BasketButtonError.SKU_OPEN_SIZE_SELECT;
        }
        else {
            return BasketButtonError.SKU;
        }
    }
    return null;
};
export function* getBasketSaga() {
    yield throttle(2000, BasketActionTypes.GET, getBasket);
}
export function* getBasicBasketSaga() {
    yield takeLatest(BasketActionTypes.GET_BASIC, getBasicBasket);
}
export function* addProductToBasketSaga() {
    yield takeLatest(BasketActionTypes.ADD, addProductToBasket);
    yield takeLatest(BasketActionTypes.ADD_BUNDLE, addProductBundleDealsToBasket);
    yield takeLatest(BasketActionTypes.BASIC_ADD, addProductToBasket);
    yield takeLatest(BasketActionTypes.ADD_VOUCHER, addProductVoucherToBasket);
    yield takeLatest(BasketActionTypes.ADD_PROMOTION, addProductPromotionToBasket);
    yield takeLatest(BasketActionTypes.ADD_GIFT_CARD, addProductGiftCardToBasket);
    yield takeLatest(BasketActionTypes.ADD_EXTENSIVE_TILE_PRODUCT, addExtensiveTileProductToBasket);
    yield takeLatest(BasketActionTypes.ADD_BULK, addBulkProductsToBasket);
    yield takeLatest(BasketActionTypes.ADD_ADDITIONAL_PRODUCTS, addAdditionalProductsToBasket);
}
export function* closeBasketPopupSaga() {
    yield takeLatest(BasketActionTypes.POPUP_CLOSE, closeBasketPopup);
}
export function* showBasketPopupSaga() {
    yield takeLatest(BasketActionTypes.POPUP_SHOW, showBasketPopup);
}
export function* showSizeForgottenTooltipSaga() {
    yield takeLatest(BasketActionTypes.SIZE_FORGOTTEN_SHOW, showSizeForgottenTooltip);
}
export function* getBasketAnalyticsSaga() {
    yield takeLatest(BasketActionTypes.ADD_SUCCESS, getBasketAnalytics);
}
export function* getBundleBasketAnalyticsSaga() {
    yield takeLatest(BasketActionTypes.ADD_BUNDLE_SUCCESS, getBundleBasketAnalytics);
}
export function* updateBasketQuantitySaga() {
    yield takeLatest(BasketActionTypes.UPDATE_QUANTITY, updateBasketQuantity);
}
export function* customEventSaga() {
    yield takeEvery(GeneralActionTypes.CUSTOM_EVENT, handleCustomEvent);
}
export function* basketPopupClose() {
    yield takeEvery(BasketActionTypes.POPUP_CLOSE, onBasketPopupClose);
}
export function* onBasketPopupClose() {
    yield MonetateUtil.pushPageEvent();
}
export function* handleCustomEvent(action) {
    switch (action.payload.eventType) {
        case EventEnum.BASKET_CHANGED:
        case EventEnum.LOGIN_SUCCESS:
            yield spawn(getBasicBasket);
            break;
        default:
            break;
    }
}
export function* showBasketPopup(action) {
    switch (action.payload) {
        case 'ERROR':
            yield fork(openBasketPopup, BasketUpdateStatus.ERROR);
            break;
        case 'OUTOFSTOCK':
            yield fork(openBasketPopup, BasketUpdateStatus.OUTOFSTOCK);
            break;
        default:
            yield fork(openBasketPopup, BasketUpdateStatus.SUCCESS, 1);
    }
}
export function* showSizeForgottenTooltip(action) {
    const sku = yield select(getSelectedSku);
    const basketDataErrorState = validateBasketData(sku, action === null || action === void 0 ? void 0 : action.payload);
    if (basketDataErrorState !== null) {
        yield fork(showAndHideBasketError, basketDataErrorState);
    }
}
export function* openBasketPopup(status, quantity, errorCode) {
    let basketProductData = yield select(getProductData);
    basketProductData = Object.assign(Object.assign(Object.assign({}, basketProductData), { productType: ProductTypes.PRODUCT }), (quantity && { quantity }));
    EventUtil.dispatch(EventEnum.BASKET_POPUP_VISIBLE, true);
    yield put(basketActions.createPopupOpenAction({
        basketProductData,
        status,
        errorCode,
    }));
}
export function* openBasketPopupForBundle(status, basketProductData, price, errorCode) {
    EventUtil.dispatch(EventEnum.BASKET_POPUP_VISIBLE, true);
    yield put(basketActions.createPopupOpenAction({
        basketProductData,
        status,
        errorCode,
        price,
    }));
}
export function* openBasketPopupForVoucher(status, basketProductData, errorCode) {
    EventUtil.dispatch(EventEnum.BASKET_POPUP_VISIBLE, true);
    yield put(basketActions.createPopupOpenAction({
        basketProductData,
        status,
        errorCode,
    }));
}
export function* openBasketPopupForExtensiveTileProduct(status, addToBasketData, product, errorCode) {
    var _a, _b;
    const selectedColour = product.colours.find((colour) => colour.colourId === addToBasketData.colorId);
    const selectedSize = selectedColour.sizes.find((size) => size.sku === addToBasketData.sku);
    const sizeDescription = getSizeDescription(selectedSize);
    const basketProductData = {
        description: product.title,
        colorDescription: selectedColour.description,
        sizeDescription,
        image: selectedColour.image,
        productCode: addToBasketData.productCode,
        quantity: addToBasketData.quantity,
        productId: addToBasketData.productId,
        brand: product.impression && product.impression.brand,
        colorId: selectedColour.colourId,
        prices: selectedSize.prices || selectedColour.prices,
        seoUrl: product.seoUrl,
        swatch: selectedColour.swatch,
        sku: Number(addToBasketData.sku),
        productHasDiscount: selectedColour.hasDiscount,
        productDiscountPercentage: (_a = selectedColour.price.productDiscountPercentage) !== null && _a !== void 0 ? _a : undefined,
        productDiscountAmount: (_b = selectedColour.price.productDiscountAmount) !== null && _b !== void 0 ? _b : undefined,
        productType: ProductTypes.PRODUCT,
    };
    EventUtil.dispatch(EventEnum.BASKET_POPUP_VISIBLE, true);
    yield put(basketActions.createPopupOpenAction({
        basketProductData,
        status,
        errorCode,
    }));
}
export function* openBasketPopupForAdditionalProducts(activeProduct, products, basketProducts, status, errorCode) {
    const productsData = products.map(additionalProduct => {
        var _a, _b, _c, _d, _e;
        const { product, displayedColor } = additionalProduct;
        const selectedSize = displayedColor.sizes[0];
        if (!selectedSize) {
            return;
        }
        const sizeDescription = getSizeDescription(selectedSize);
        return {
            description: product.title,
            colorDescription: displayedColor.description,
            sizeDescription,
            image: displayedColor.image,
            productCode: product.productCode,
            quantity: 1,
            productId: String(product.productId),
            brand: ((_a = product.impression) === null || _a === void 0 ? void 0 : _a.brand) || product.brand,
            colorId: displayedColor.colourId,
            prices: selectedSize.prices || displayedColor.prices,
            seoUrl: product.seoUrl,
            swatch: displayedColor.swatch,
            sku: Number(selectedSize.sku),
            productHasDiscount: displayedColor.hasDiscount,
            productDiscountPercentage: (_c = (_b = displayedColor.price) === null || _b === void 0 ? void 0 : _b.productDiscountPercentage) !== null && _c !== void 0 ? _c : undefined,
            productDiscountAmount: (_e = (_d = displayedColor.price) === null || _d === void 0 ? void 0 : _d.productDiscountAmount) !== null && _e !== void 0 ? _e : undefined,
            productType: ProductTypes.PRODUCT,
        };
    });
    const basketProductData = [activeProduct, ...productsData].filter(Boolean).map(product => {
        var _a;
        return Object.assign(Object.assign({}, product), { success: status !== BasketUpdateStatus.ERROR
                ? (_a = basketProducts.find(basketProduct => basketProduct.productId === Number(product.productId))) === null || _a === void 0 ? void 0 : _a.success
                : undefined });
    });
    EventUtil.dispatch(EventEnum.BASKET_POPUP_VISIBLE, true);
    yield put(basketActions.createPopupOpenAction({
        basketProductData,
        status,
        isBulk: true,
        errorCode,
    }));
}
export function* openBasketPopupForBulkProducts(products, basketProducts, status, errorCode) {
    const basketProductData = products.map(({ product, colorId, sku, quantity }, index) => {
        var _a, _b, _c, _d;
        const selectedColour = product.colours.find(colour => colour.colourId === colorId);
        const selectedSize = selectedColour.sizes.find(size => size.sku === sku);
        const sizeDescription = getSizeDescription(selectedSize);
        return {
            description: product.title,
            colorDescription: selectedColour.description,
            sizeDescription,
            image: selectedColour.image,
            productCode: product.productCode,
            quantity,
            productId: String(product.productId),
            brand: ((_a = product.impression) === null || _a === void 0 ? void 0 : _a.brand) || product.brand,
            colorId: selectedColour.colourId,
            prices: selectedSize.prices || selectedColour.prices,
            seoUrl: product.seoUrl,
            swatch: selectedColour.swatch,
            sku: Number(sku),
            productHasDiscount: selectedColour.hasDiscount,
            productDiscountPercentage: (_b = selectedColour.price.productDiscountPercentage) !== null && _b !== void 0 ? _b : undefined,
            productDiscountAmount: (_c = selectedColour.price.productDiscountAmount) !== null && _c !== void 0 ? _c : undefined,
            productType: ProductTypes.PRODUCT,
            success: status !== BasketUpdateStatus.ERROR ? (_d = basketProducts[index]) === null || _d === void 0 ? void 0 : _d.success : undefined,
        };
    });
    EventUtil.dispatch(EventEnum.BASKET_POPUP_VISIBLE, true);
    yield put(basketActions.createPopupOpenAction({
        basketProductData,
        status,
        isBulk: true,
        errorCode,
    }));
}
export function* closeBasketPopup() {
    yield call(EventUtil.dispatch, EventEnum.BASKET_POPUP_VISIBLE, false);
}
export function* showAndHideBasketError(error) {
    yield put(basketActions.createErrorAction(error));
    yield delay(DELAY.DELAY_3000);
    yield put(basketActions.createErrorAction(undefined));
}
export function* addProductVoucherToBasket(action) {
    const addToBasketData = yield select(getAddToBasketVoucherData);
    const defaultSize = getDefaultSize(addToBasketData.productColorVariations, addToBasketData.colorId, action.payload.voucherSizeCode);
    const requestParams = {
        sku: defaultSize.sku,
        productId: addToBasketData.productId,
        productCode: addToBasketData.productCode,
        colorId: addToBasketData.colorId,
        quantity: action.payload.quantity,
        productPriceManual: action.payload.amount,
        additionalProperties: {
            message: action.payload.message,
            layout: action.payload.imageTemplateName,
            image: action.payload.image,
        },
    };
    const basketProductData = {
        productType: ProductTypes.E_VOUCHER,
        description: addToBasketData.title,
        brand: addToBasketData.brand,
        quantity: action.payload.quantity,
        image: action.payload.image,
        productCode: addToBasketData.productCode,
        message: action.payload.message,
        productHasDiscount: false,
        price: action.payload.amount,
        productId: addToBasketData.productId,
    };
    try {
        const { servicesEndpoint, defaultRequestParams } = yield getContext('aemConfiguration');
        yield call(BasketAPI.addProductToBasket, requestParams, servicesEndpoint, defaultRequestParams);
        yield put(basketActions.createAddSuccessAction({ product: requestParams }));
        EventUtil.dispatch(EventEnum.BASKET_CHANGED);
        yield spawn(openBasketPopupForVoucher, BasketUpdateStatus.SUCCESS, basketProductData);
        yield put(basketActions.createGetAction());
    }
    catch (e) {
        const { response } = e;
        const responseData = (response === null || response === void 0 ? void 0 : response.data) || null;
        yield spawn(openBasketPopupForVoucher, BasketUpdateStatus.ERROR, basketProductData, responseData ? responseData.code : null);
    }
}
export function* addProductPromotionToBasket(action) {
    const requestParams = action.payload;
    EventUtil.dispatch(EventEnum.BASKET_PROMOTION_CALLED);
    try {
        const { servicesEndpoint, defaultRequestParams } = yield getContext('aemConfiguration');
        yield call(BasketAPI.addProductToBasket, requestParams, servicesEndpoint, defaultRequestParams);
        yield put(basketActions.createAddSuccessAction({ product: requestParams }));
        EventUtil.dispatch(EventEnum.BASKET_CHANGED);
        yield put(basketActions.createGetAction());
    }
    catch (_a) { }
}
export function* addProductGiftCardToBasket(action) {
    const addToBasketData = yield select(getAddToBasketVoucherData);
    const defaultColorVariation = findColor(addToBasketData.productColorVariations, addToBasketData.colorId);
    const defaultSize = findSize(defaultColorVariation.sizes, action.payload.giftCardSizeCode);
    const requestParams = {
        sku: defaultSize.sku,
        productId: addToBasketData.productId,
        productCode: addToBasketData.productCode,
        colorId: addToBasketData.colorId,
        quantity: action.payload.quantity,
        productPriceManual: action.payload.amount,
    };
    const basketProductData = {
        productType: ProductTypes.GIFT_CARD,
        description: addToBasketData.title,
        quantity: action.payload.quantity,
        image: defaultColorVariation.images[0].picture,
        productCode: addToBasketData.productCode,
        productHasDiscount: false,
        price: action.payload.amount,
        productId: addToBasketData.productId,
    };
    try {
        const { servicesEndpoint, defaultRequestParams } = yield getContext('aemConfiguration');
        yield call(BasketAPI.addProductToBasket, requestParams, servicesEndpoint, defaultRequestParams);
        yield put(basketActions.createAddSuccessAction({ product: requestParams }));
        EventUtil.dispatch(EventEnum.BASKET_CHANGED);
        yield spawn(openBasketPopupForVoucher, BasketUpdateStatus.SUCCESS, basketProductData);
        yield put(basketActions.createGetAction());
    }
    catch (e) {
        const { response } = e;
        const responseData = (response === null || response === void 0 ? void 0 : response.data) || null;
        yield spawn(openBasketPopupForVoucher, BasketUpdateStatus.ERROR, basketProductData, responseData ? responseData.code : null);
    }
}
export function* addExtensiveTileProductToBasket(action) {
    const { productData, location, analyticsData } = action.payload;
    const { product, colorId, sku, hideBasketPopup } = productData;
    const addToBasketData = {
        productCode: product.productCode,
        productId: product.productId.toString(),
        colorId,
        sku,
        quantity: 1,
    };
    try {
        const { pageInfo, servicesEndpoint, defaultRequestParams } = yield getContext('aemConfiguration');
        yield call(BasketAPI.addProductToBasket, addToBasketData, servicesEndpoint, defaultRequestParams);
        yield put(basketActions.createAddSuccessAction({
            product: addToBasketData,
            location: location
                ? location
                : pageInfo.isBasketOverviewPage
                    ? ProductAddToBasketLocations.BASKET_RECOMMENDATIONS
                    : undefined,
            analyticsData,
        }));
        yield put(basketActions.createGetBasicAction());
        yield put(basketActions.createGetAction());
        EventUtil.dispatch(EventEnum.BASKET_CHANGED);
        if (!hideBasketPopup) {
            yield spawn(openBasketPopupForExtensiveTileProduct, BasketUpdateStatus.SUCCESS, addToBasketData, product);
        }
    }
    catch (error) {
        const { response } = error;
        const responseData = (response === null || response === void 0 ? void 0 : response.data) || null;
        if (responseData && responseData.availableQuantity === 0) {
            yield spawn(openBasketPopupForExtensiveTileProduct, BasketUpdateStatus.OUTOFSTOCK, addToBasketData, product);
        }
        else {
            yield spawn(openBasketPopupForExtensiveTileProduct, BasketUpdateStatus.ERROR, addToBasketData, product, responseData ? responseData.code : undefined);
        }
    }
}
export function* addProductToBasket(action) {
    const { servicesEndpoint, defaultRequestParams } = yield getContext('aemConfiguration');
    const { storeSourceExcluded } = yield select(makeGetProductFulfillmentBySize());
    let addToBasketData = yield select(getAddToBasketData);
    addToBasketData = Object.assign(Object.assign({}, addToBasketData), { quantity: action.payload.amount });
    const basketDataErrorState = validateBasketData(addToBasketData, action.payload.location, true);
    if (storeSourceExcluded && action.payload.location !== ProductAddToBasketLocations.CLICK_AND_COLLECT) {
        yield spawn(openBasketPopup, BasketUpdateStatus.ERROR, addToBasketData.quantity, BasketResponseCode.SS_EXCLUDED);
        return;
    }
    if (basketDataErrorState !== null) {
        yield fork(showAndHideBasketError, basketDataErrorState);
    }
    else {
        try {
            yield call(BasketAPI.addProductToBasket, addToBasketData, servicesEndpoint, defaultRequestParams);
            yield put(basketActions.createAddSuccessAction({
                product: addToBasketData,
                pdpVersion: action.type === BasketActionTypes.ADD ? action.payload.pdpVersion : undefined,
            }));
            EventUtil.dispatch(EventEnum.BASKET_CHANGED);
            if (action.type === BasketActionTypes.BASIC_ADD) {
                yield put(basketActions.createStatusUpdateAction({
                    status: BasketUpdateStatus.SUCCESS,
                }));
                if (action.payload.updateBasket) {
                    yield put(basketActions.createGetAction());
                }
            }
            else {
                yield spawn(openBasketPopup, BasketUpdateStatus.SUCCESS, addToBasketData.quantity);
                yield put(basketActions.createGetAction());
            }
        }
        catch (e) {
            const { response } = e;
            const responseData = (response === null || response === void 0 ? void 0 : response.data) || null;
            if (responseData && responseData.availableQuantity === 0 && action.type === BasketActionTypes.BASIC_ADD) {
                yield put(basketActions.createStatusUpdateAction({
                    status: BasketUpdateStatus.OUTOFSTOCK,
                }));
            }
            else if (responseData && responseData.availableQuantity === 0) {
                yield spawn(openBasketPopup, BasketUpdateStatus.OUTOFSTOCK, addToBasketData.quantity);
            }
            else if (responseData && responseData.availableQuantity > 0 && action.type === BasketActionTypes.ADD) {
                yield put(basketActions.createAddSuccessAction({
                    product: Object.assign(Object.assign({}, addToBasketData), { quantity: responseData.availableQuantity }),
                    pdpVersion: action.payload.pdpVersion,
                }));
                EventUtil.dispatch(EventEnum.BASKET_CHANGED);
                yield spawn(openBasketPopup, BasketUpdateStatus.SUCCESS, responseData.availableQuantity);
                yield put(basketActions.createGetAction());
            }
            else if (action.type === BasketActionTypes.BASIC_ADD) {
                yield put(basketActions.createStatusUpdateAction({
                    status: BasketUpdateStatus.ERROR,
                    errorCode: responseData ? responseData.code : undefined,
                }));
            }
            else {
                yield spawn(openBasketPopup, BasketUpdateStatus.ERROR, addToBasketData.quantity, responseData ? responseData.code : undefined);
            }
        }
    }
}
export function* addProductBundleDealsToBasket(action) {
    var _a;
    const { defaultRequestParams, servicesEndpoint } = yield getContext('aemConfiguration');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const _b = yield select(makeGetBundleParentProduct()), { bundle } = _b, parentData = __rest(_b, ["bundle"]);
    const parentSize = parentData.sizes.find(size => size.sku === parentData.selectedSku);
    const selectedSlots = bundle.bundleSlots.filter(slot => slot.isSelected);
    const addToBasketData = [
        {
            colorId: parentData.activeColor.colorId,
            productCode: bundle.productCode,
            productId: bundle.productId,
            quantity: 1,
            sku: parentData.selectedSku,
        },
        ...selectedSlots.map(slot => ({
            colorId: slot.displayedProduct.colourId,
            productCode: slot.displayedProduct.productCode,
            productId: slot.displayedProduct.productId,
            quantity: 1,
            sku: slot.displayedProduct.skuId,
        })),
    ];
    const basketProductData = [
        {
            brand: parentData.brand,
            description: parentData.title,
            image: bundle.image,
            productCode: parentData.productCode,
            sku: parentData.selectedSku,
            sizeDescription: getSizeDescription(parentSize),
            originalSize: getSizeDescription(parentSize, true),
            colorDescription: parentData.activeColor.translations.description,
            quantity: 1,
            productType: ProductTypes.PRODUCT,
        },
        ...selectedSlots.map(slot => {
            const selectedProduct = slot.bundleChildren.find(product => product.productCode === slot.displayedProduct.productCode);
            const selectedColor = selectedProduct.colours.find(color => color.colourId === slot.displayedProduct.colourId);
            const selectedSize = selectedColor.sizes.find(size => `${size.sku}` === `${slot.displayedProduct.skuId}`);
            return {
                brand: selectedProduct.brand,
                description: selectedProduct.title,
                image: selectedColor.image,
                productCode: selectedProduct.productCode,
                sizeDescription: getSizeDescription(selectedSize),
                originalSize: getSizeDescription(selectedSize, true),
                colorDescription: selectedColor.description,
                quantity: 1,
                productType: ProductTypes.PRODUCT,
                sku: parseInt(selectedSize.sku, 10),
            };
        }),
    ];
    const bundlePrice = (_a = bundle.calculations) === null || _a === void 0 ? void 0 : _a.data.bundleCalculationPrice;
    const basketPrice = {
        prices: bundlePrice.totalPrices,
        hasDiscount: bundlePrice.totalDiscountPercentage > 0,
        bundleDiscount: bundlePrice.bundleChildrenDiscountPct,
        productDiscountPercentage: bundlePrice.totalDiscountPercentage,
        productDiscountAmount: bundlePrice.totalDiscountAmount,
    };
    try {
        yield call(BasketAPI.addBundleToBasket, addToBasketData, servicesEndpoint, defaultRequestParams);
        yield put(basketActions.createAddBundleSuccessAction(addToBasketData, action.payload.location));
        EventUtil.dispatch(EventEnum.BASKET_CHANGED);
        yield spawn(openBasketPopupForBundle, BasketUpdateStatus.SUCCESS, basketProductData, basketPrice);
        yield put(basketActions.createGetAction());
    }
    catch (e) {
        const { response } = e;
        const responseData = response === null || response === void 0 ? void 0 : response.data;
        const unavailableProducts = Array.isArray(responseData)
            ? responseData.find(basketResponseItem => basketResponseItem.availableQuantity === 0)
            : [];
        if (responseData && responseData.availableQuantity === 0 && action.type === BasketActionTypes.ADD_BUNDLE) {
            const updatedBasketProductData = basketProductData.map(data => {
                if (data.sku === responseData.skuId) {
                    return Object.assign(Object.assign({}, data), responseData);
                }
                return data;
            });
            yield spawn(openBasketPopupForBundle, BasketUpdateStatus.BUNDLE_OUTOFSTOCK, updatedBasketProductData, basketPrice, responseData ? responseData.code : null);
        }
        else if (responseData && unavailableProducts.length > 0 && action.type === BasketActionTypes.ADD_BUNDLE) {
            const updatedBasketProductData = basketProductData.map(data => {
                if (data.sku === responseData.skuId) {
                    return Object.assign(Object.assign({}, data), responseData);
                }
                return data;
            });
            yield spawn(openBasketPopupForBundle, BasketUpdateStatus.BUNDLE_OUTOFSTOCK, updatedBasketProductData, basketPrice, responseData ? responseData.code : null);
        }
        else if (action.type === BasketActionTypes.ADD_BUNDLE) {
            yield spawn(openBasketPopupForBundle, BasketUpdateStatus.ERROR, basketProductData, basketPrice, responseData ? responseData.code : null);
        }
    }
}
export function* addAdditionalProductsToBasket(action) {
    const { products, activeProductQuantity, location } = action.payload;
    const { defaultRequestParams, servicesEndpoint } = yield getContext('aemConfiguration');
    let addToBasketActiveProductData = yield select(getAddToBasketData);
    addToBasketActiveProductData = Object.assign(Object.assign({}, addToBasketActiveProductData), { quantity: activeProductQuantity });
    const basketDataErrorState = validateBasketData(addToBasketActiveProductData, location, true);
    if (basketDataErrorState !== null) {
        yield fork(showAndHideBasketError, basketDataErrorState);
    }
    else {
        const activeProduct = yield select(getProductData);
        try {
            const addToBasketData = products.map(additionalProduct => {
                var _a;
                const { product, displayedColor } = additionalProduct;
                return {
                    productCode: product.productCode,
                    productId: String(product.productId),
                    colorId: displayedColor.colourId,
                    sku: (_a = displayedColor.sizes) === null || _a === void 0 ? void 0 : _a[0].sku,
                    quantity: 1,
                };
            });
            const addBulkProductsData = [addToBasketActiveProductData, ...addToBasketData];
            const basketProducts = yield call(BasketAPI.addBulkProductsToBasket, addBulkProductsData, servicesEndpoint, defaultRequestParams);
            EventUtil.dispatch(EventEnum.BASKET_CHANGED);
            const status = basketProducts.some(product => !product.success)
                ? BasketUpdateStatus.PARTIAL_SUCCESS
                : BasketUpdateStatus.SUCCESS;
            const addToBasketDataSuccess = addBulkProductsData.filter(product => {
                const currentBasketProduct = basketProducts.find(basketProduct => basketProduct.productId === Number(product.productId) && basketProduct.skuId === Number(product.sku));
                return currentBasketProduct.success;
            });
            const successfullAdditionalProducts = products.filter(product => !addToBasketDataSuccess.some(successProduct => successProduct.productId === product.product.productId));
            yield spawn(getAdditionalProductBasketAnalytics, addToBasketDataSuccess, successfullAdditionalProducts, ProductAddToBasketLocations.PDP_ADDITIONAL_PRODUCTS);
            yield spawn(openBasketPopupForAdditionalProducts, activeProduct, products, basketProducts, status);
            yield put(basketActions.createGetAction());
        }
        catch (e) {
            const { response } = e;
            const responseData = response === null || response === void 0 ? void 0 : response.data;
            if (Array.isArray(responseData) && responseData.some(item => item.availableQuantity === 0)) {
                yield spawn(openBasketPopupForAdditionalProducts, activeProduct, products, responseData, BasketUpdateStatus.BULK_OUTOFSTOCK);
            }
            else {
                yield spawn(openBasketPopupForAdditionalProducts, activeProduct, products, responseData || [], BasketUpdateStatus.ERROR, responseData === null || responseData === void 0 ? void 0 : responseData.code);
            }
        }
    }
}
const toAddRequest = (item) => ({
    productCode: item.product.productCode,
    productId: String(item.product.productId),
    colorId: item.colorId,
    sku: item.sku,
    quantity: item.quantity,
});
const buildFailureResponse = (item, availableQuantity) => ({
    amountUnableToReserve: item.quantity,
    availableQuantity,
    productId: Number(item.product.productId),
    requestedQuantity: item.quantity,
    skuId: Number(item.sku),
    success: false,
});
const buildBulkCartUpdatePayload = (item, mappedProducts, totalQuantity, pricing) => {
    var _a, _b, _c;
    const color = (_a = item.product.colours) === null || _a === void 0 ? void 0 : _a.find(c => c.colourId === item.colorId);
    const size = (_b = color === null || color === void 0 ? void 0 : color.sizes) === null || _b === void 0 ? void 0 : _b.find(s => s.sku === item.sku);
    const tile = mappedProducts.find(t => String(t.sku) === String(item.sku));
    return buildCartUpdatePayload('add', {
        productId: String(item.product.productId),
        sku: item.sku,
        quantity: item.quantity,
        brand: item.product.brand,
        title: item.product.title,
        colour: color === null || color === void 0 ? void 0 : color.description,
        colourCode: color === null || color === void 0 ? void 0 : color.colourCode,
        gender: item.product.genderCode,
        size: (_c = size === null || size === void 0 ? void 0 : size.fullCommercialSize) !== null && _c !== void 0 ? _c : tile === null || tile === void 0 ? void 0 : tile.sizeDescription,
        buyingGroup: tile === null || tile === void 0 ? void 0 : tile.buyingGroup,
    }, mappedProducts, totalQuantity, pricing);
};
export function* emitItemCartUpdate(item) {
    try {
        const mappedProducts = yield select(makeGetBasketMappedProductsState());
        const totalQuantity = yield select(makeGetBasketTotalQuantityState());
        const pricing = yield select(makeGetBasketPricingState());
        const basketId = yield select(makeGetBasketId());
        emitter.emit(EmitterEvent.CART_UPDATE, Object.assign(Object.assign({}, buildBulkCartUpdatePayload(item, mappedProducts, totalQuantity, pricing)), { basket_token: basketId !== null && basketId !== void 0 ? basketId : '' }));
    }
    catch (_a) {
        // Tracking must never block the basket flow.
    }
}
export function* addBulkProductsToBasket(action) {
    var _a, _b;
    const { products, totalQuantityProducts, location } = action.payload;
    const { defaultRequestParams, servicesEndpoint } = yield getContext('aemConfiguration');
    // Sequential single-adds (one API call + refresh per item) so each cart_update event
    // reflects real post-add basket state. The bulk API would be more efficient but
    // collapses all items into a single server transaction, losing per-item progression.
    const basketResponses = [];
    for (const item of products) {
        try {
            const response = yield call(BasketAPI.addProductToBasket, toAddRequest(item), servicesEndpoint, defaultRequestParams);
            basketResponses.push(Object.assign(Object.assign({}, response), { success: true }));
            // Direct call to getBasket (instead of dispatching GET) to bypass the 2s throttle
            // on BasketActionTypes.GET, which would drop refreshes between iterations.
            yield call(getBasket);
            yield call(emitItemCartUpdate, item);
        }
        catch (error) {
            const { response } = error;
            basketResponses.push(buildFailureResponse(item, (_b = (_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.availableQuantity) !== null && _b !== void 0 ? _b : 0));
        }
    }
    EventUtil.dispatch(EventEnum.BASKET_CHANGED);
    const successfulRequests = products.filter((_, i) => { var _a; return (_a = basketResponses[i]) === null || _a === void 0 ? void 0 : _a.success; }).map(toAddRequest);
    const allFailedOutOfStock = basketResponses.length > 0 && basketResponses.every(r => !r.success && r.availableQuantity === 0);
    const hasFailure = basketResponses.some(r => !r.success);
    const status = allFailedOutOfStock
        ? BasketUpdateStatus.BULK_OUTOFSTOCK
        : hasFailure
            ? BasketUpdateStatus.PARTIAL_SUCCESS
            : BasketUpdateStatus.SUCCESS;
    yield spawn(getBulkBasketAnalytics, successfulRequests, location, `${successfulRequests.length}/${totalQuantityProducts}`);
    yield spawn(openBasketPopupForBulkProducts, products, basketResponses, status);
}
export function* getBasket() {
    try {
        const { currency, servicesEndpoint, defaultRequestParams, lang } = yield getContext('aemConfiguration');
        yield call(checkStockReservation);
        const basketData = yield call(BasketAPI.getBasket, servicesEndpoint, defaultRequestParams);
        if (!!basketData &&
            defaultRequestParams.shopId &&
            defaultRequestParams.shopId !== basketData.fictionalId.toString()) {
            yield call(BasketAPI.shopAndFictionalIdCheck, servicesEndpoint, defaultRequestParams);
        }
        if (!basketData) {
            yield put(basketActions.createSetAction({
                status: BasketStatus.EMPTY,
            }));
            MonetateUtil.pushBasketEvent([], currency);
        }
        else if (basketData.language && basketData.language !== lang) {
            yield fork(updateBasketLanguage, lang, true);
        }
        else {
            yield call(setBasketData, basketData);
        }
    }
    catch (_a) {
        yield put(basketActions.createSetAction({
            status: BasketStatus.ERROR,
        }));
    }
}
export function* setBasketData(basketData) {
    try {
        const aemConfigurationContext = yield getContext('aemConfiguration');
        const currentProducts = yield select(getCurrentProducts);
        const currentMappedProducts = yield select(getCurrentMappedProducts);
        const { currency } = aemConfigurationContext;
        const basketPopupState = yield select(makeGetBasketPopupState());
        const addedProducts = basketData.products.filter(product => !currentProducts.find((currentProduct) => {
            var _a;
            return ((_a = currentProduct.additionalProperties) === null || _a === void 0 ? void 0 : _a.eVoucherId)
                ? currentProduct.additionalProperties.eVoucherId === product.additionalProperties.eVoucherId
                : currentProduct.sku === product.sku;
        }));
        const currentMappedProductsWithErrorState = currentMappedProducts.reduce((prevProduct, currentProduct) => currentProduct.errorState ? prevProduct.concat(currentProduct.productCode) : prevProduct, []);
        const newMappedProducts = yield all(addedProducts.map((product) => call(ProductDetailAPI.fetchProductForBasket, aemConfigurationContext.servicesApi, aemConfigurationContext.servicesApiPrefixAemServices, aemConfigurationContext.mainWebShop, aemConfigurationContext.fictiveWebShop, aemConfigurationContext.lang, product)));
        const tempMappedProducts = currentMappedProducts
            .filter((mappedProduct) => basketData.products.find((product) => mappedProduct.eVoucherId
            ? product.additionalProperties.eVoucherId === mappedProduct.additionalProperties.eVoucherId
            : product.sku === mappedProduct.sku))
            .concat(newMappedProducts);
        const mappedProducts = basketData.products.map((baseProduct) => {
            const mappedProduct = tempMappedProducts
                .map((product) => {
                var _a;
                const isProductHasErrorState = currentMappedProductsWithErrorState.includes(product.productCode);
                const actualProductErrorState = (_a = currentMappedProducts.find((currentProduct) => currentProduct.productCode === product.productCode)) === null || _a === void 0 ? void 0 : _a.errorState;
                return isProductHasErrorState
                    ? Object.assign(Object.assign({}, product), { errorState: actualProductErrorState }) : product;
            })
                .find((product) => baseProduct.additionalProperties.eVoucherId
                ? product.eVoucherId === baseProduct.additionalProperties.eVoucherId
                : baseProduct.sku === product.sku);
            return Object.assign(Object.assign({}, mappedProduct), { sseProduct: baseProduct.sseProduct, specialtyStoreProduct: baseProduct.specialtyStoreProduct, availableInSSEStore: baseProduct.availableInSSEStore, availableInSpecialtyStore: baseProduct.availableInSpecialtyStore, ccExcluded: baseProduct.ccExcluded, quantity: baseProduct.quantity, price: baseProduct.productPriceSell, previousPrice: baseProduct.productPriceStandard, totalPrice: baseProduct.totalProductPriceSell, totalPreviousPrice: baseProduct.totalProductPriceStandard, priceManual: baseProduct.productPriceManual, productDiscountAmount: baseProduct.productDiscountAmount, productDiscountPercentage: baseProduct.productDiscountPercentage, productHasDiscount: baseProduct.productDiscountPercentage > 0, additionalProperties: baseProduct.additionalProperties, prices: baseProduct.prices, totalProductDiscountAmount: baseProduct.totalProductDiscountAmount, promotion: baseProduct.promotion, features: baseProduct.features });
        });
        let mappedProductsWithAvailabilities = [];
        if (basketData.totalQuantity > 0) {
            const productAvailabilities = yield call(ProductDetailAPI.getProductBasketAvailabilitiesByProductId, aemConfigurationContext.servicesApi, aemConfigurationContext.mainWebShop, mappedProducts.map(product => product.productId).join(','));
            mappedProductsWithAvailabilities = productAvailabilities
                ? BasketUtil.mergeProductAvailabilitiesToBasketData(mappedProducts, productAvailabilities)
                : mappedProducts;
        }
        const data = {
            deliveryInfo: {
                amountUntilFreeDelivery: basketData.amountUntilFreeDelivery,
                amountUntilMinimumOrderValue: basketData.amountUntilMinimumOrderValue,
                delivery: basketData.delivery,
                deliveryMessages: basketData.deliveryMessages,
                deliveryUnit: basketData.deliveryUnit,
                hasEvoucher: basketData.hasEvoucher,
                minimumOrderValue: basketData.minimumOrderValue,
                requiresDelivery: basketData.requiresDelivery,
                deliveryPromiseCutoff: basketData.deliveryPromiseCutoff,
                hasDeliveryMessage: basketData.hasDeliveryMessage,
            },
            products: basketData.products,
            mappedProducts: mappedProductsWithAvailabilities,
            promotionProducts: basketData.products.filter(product => product.promotion),
            pricing: {
                priceDelivery: basketData.priceDelivery,
                priceTotalBasket: basketData.priceTotalBasket,
                totalPriceSell: basketData.totalPriceSell,
                totalBasketDiscountAmount: basketData.totalBasketDiscountAmount,
                totalPriceSellBeforeDiscount: basketData.totalPriceSellBeforeDiscount,
                totalPendingPaymentAmount: basketData.totalPendingPaymentAmount,
                priceTotalBasketMinusVouchers: basketData.priceTotalBasketMinusVouchers,
            },
            status: basketData.totalQuantity > 0 ? BasketStatus.READY : BasketStatus.EMPTY,
            vouchers: basketData.vouchers,
            validateRegex: basketData.validateRegex,
            popupMessages: basketData.popupMessages,
            popupMessagesEnriched: basketData.popupMessagesEnriched,
            paybackVoucherMessages: basketData.paybackVoucherMessages,
            orderLinesHaveDifferentDeliveryPromises: basketData.orderLinesHaveDifferentDeliveryPromises,
            totalQuantity: basketData.totalQuantity,
            clickAndCollectStoreId: basketData.clickAndCollectStoreId,
            totalVoucherAmount: basketData.totalVoucherAmount,
            isBasketLocked: basketData.locked,
        };
        yield put(basketActions.createSetAction(data));
        yield put(basketActions.createAdobeAnalyticsTokenSetAction(basketData.basketId));
        yield put(basketActions.createRefreshCompleteAction());
        MonetateUtil.pushBasketEvent(data.mappedProducts, currency, basketPopupState.collapsed ? undefined : 'addtocart');
    }
    catch (_a) {
        yield put(basketActions.createSetAction({
            status: BasketStatus.ERROR,
        }));
    }
}
export function* updateBasketLanguage(language, shouldGetBasket) {
    const { defaultRequestParams, servicesEndpoint } = yield getContext('aemConfiguration');
    yield call(BasketAPI.updateLanguage, language, servicesEndpoint, defaultRequestParams);
    if (shouldGetBasket) {
        yield fork(getBasket);
    }
}
export function* updateBasketQuantity(action) {
    const { sku } = yield select(getSelectedSku);
    if (!sku) {
        yield fork(showAndHideBasketError, BasketButtonError.SKU);
    }
    else {
        try {
            const { defaultRequestParams, servicesEndpoint } = yield getContext('aemConfiguration');
            const addToBasketData = yield select(getAddToBasketData);
            const requestParams = {
                sku: addToBasketData.sku,
                productId: addToBasketData.productId,
                productCode: addToBasketData.productCode,
                colorId: addToBasketData.colorId,
                quantity: Number(action.payload),
                productPriceManual: 0,
                additionalProperties: {},
            };
            const product = yield call(BasketAPI.updateQuantity, requestParams, servicesEndpoint, defaultRequestParams);
            yield put(basketActions.createUpdateQuantityResultAction(product));
        }
        catch (e) {
            const { response } = e;
            if (response.data.productId) {
                yield put(basketActions.createUpdateQuantityResultAction(response.data));
            }
            yield fork(showAndHideBasketError, BasketButtonError.STOCK);
        }
    }
}
export function* getBasicBasket() {
    const aemConfigurationContext = yield getContext('aemConfiguration');
    const { pageInfo, servicesEndpoint, defaultRequestParams } = aemConfigurationContext;
    if (pageInfo.isConfirmationPage)
        return;
    try {
        let basicBasketData = yield call(BasketAPI.getBasicBasket, servicesEndpoint, defaultRequestParams);
        if (aemConfigurationContext.isShopLanding) {
            const isInactive = yield call(isInactiveShoplandingBasket, basicBasketData, aemConfigurationContext.shoplanding.timeout_reset_seconds);
            if (isInactive) {
                basicBasketData = yield call(BasketAPI.resetBasketForShopLanding, servicesEndpoint, defaultRequestParams);
            }
        }
        yield put(basketActions.createSetBasicAction(basicBasketData));
    }
    catch (_a) { }
}
export function isInactiveShoplandingBasket(basketData, timeout_reset_seconds) {
    let timeDiff = null;
    if (basketData.dateUserInteraction && basketData.currentDate) {
        const timeDiffInMs = calculateDifferenceInMs(basketData.currentDate, basketData.dateUserInteraction);
        timeDiff = timeDiffInMs ? timeDiffInMs / 1000 : null;
    }
    if (timeDiff && timeDiff > timeout_reset_seconds) {
        return true;
    }
    return false;
}
export function* getBasketAnalytics(action) {
    var _a, _b, _c;
    try {
        const { defaultRequestParams, servicesEndpoint } = yield getContext('aemConfiguration');
        const analyticsGoogleTagManagerData = yield call(BasketAPI.addProductToBasketAnalytics, action.payload.product, servicesEndpoint, defaultRequestParams);
        yield put(basketActions.createGoogleAnalyticsSuccessAction(analyticsGoogleTagManagerData));
        const analyticsAdobeTagManagerData = yield call(BasketAPI.addProductToBasketDtm, action.payload.product, servicesEndpoint, defaultRequestParams);
        if (action.payload.location && ((_a = analyticsAdobeTagManagerData.page) === null || _a === void 0 ? void 0 : _a.attributes)) {
            analyticsAdobeTagManagerData.page.attributes.addCardLocation = action.payload.location;
        }
        if (action.payload.pdpVersion === PDPVersion.V2) {
            yield put(analyticsActions.productDetailAddToBasket(analyticsAdobeTagManagerData));
        }
        else {
            if (action.payload.analyticsData && analyticsAdobeTagManagerData) {
                const { componentName, componentType, productTilePosition } = action.payload.analyticsData;
                analyticsAdobeTagManagerData.component = { componentInfo: { componentName, componentType } };
                if ((_c = (_b = analyticsAdobeTagManagerData.product) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.attributes) {
                    analyticsAdobeTagManagerData.product[0].attributes.productTilePosition = productTilePosition;
                }
            }
            yield put(basketActions.createAdobeAnalyticsSuccessAction(analyticsAdobeTagManagerData));
        }
    }
    catch (_d) { }
}
export function* getBundleBasketAnalytics(action) {
    var _a;
    try {
        const { defaultRequestParams, servicesEndpoint } = yield getContext('aemConfiguration');
        const analyticsGoogleTagManagerData = yield call(BasketAPI.addBundleToBasketAnalytics, action.payload.products, servicesEndpoint, defaultRequestParams);
        yield put(basketActions.createGoogleAnalyticsSuccessAction(analyticsGoogleTagManagerData));
        const analyticsAdobeTagManagerData = yield call(BasketAPI.addBundleToBasketDtm, action.payload.products, servicesEndpoint, defaultRequestParams);
        if (action.payload.location && ((_a = analyticsAdobeTagManagerData.page) === null || _a === void 0 ? void 0 : _a.attributes)) {
            analyticsAdobeTagManagerData.page.attributes.addCardLocation = action.payload.location;
        }
        yield put(analyticsActions.productDetailAddToBasket(analyticsAdobeTagManagerData));
    }
    catch (_b) { }
}
export function* getBulkBasketAnalytics(products, location, eventName) {
    var _a, _b;
    try {
        const { defaultRequestParams, servicesEndpoint } = yield getContext('aemConfiguration');
        const analyticsGoogleTagManagerData = yield call(BasketAPI.addBundleToBasketAnalytics, products, servicesEndpoint, defaultRequestParams);
        yield put(basketActions.createGoogleAnalyticsSuccessAction(analyticsGoogleTagManagerData));
        const analyticsAdobeTagManagerData = yield call(BasketAPI.addBundleToBasketDtm, products, servicesEndpoint, defaultRequestParams);
        if (location && ((_a = analyticsAdobeTagManagerData.page) === null || _a === void 0 ? void 0 : _a.attributes)) {
            analyticsAdobeTagManagerData.page.attributes.addCardLocation = location;
        }
        if (analyticsAdobeTagManagerData.event && ((_b = analyticsAdobeTagManagerData.event) === null || _b === void 0 ? void 0 : _b.length) > 0) {
            analyticsAdobeTagManagerData.event[0].eventInfo.eventName = eventName;
        }
        yield put(basketActions.createAdobeAnalyticsSuccessAction(analyticsAdobeTagManagerData));
    }
    catch (_c) { }
}
export function* getAdditionalProductBasketAnalytics(products, additionalProducts, location) {
    var _a;
    try {
        const { defaultRequestParams, servicesEndpoint } = yield getContext('aemConfiguration');
        const analyticsGoogleTagManagerData = yield call(BasketAPI.addBundleToBasketAnalytics, products, servicesEndpoint, defaultRequestParams);
        yield put(basketActions.createGoogleAnalyticsSuccessAction(analyticsGoogleTagManagerData));
        const analyticsAdobeTagManagerData = yield call(BasketAPI.addBundleToBasketDtm, products, servicesEndpoint, defaultRequestParams);
        if (location && ((_a = analyticsAdobeTagManagerData.page) === null || _a === void 0 ? void 0 : _a.attributes)) {
            analyticsAdobeTagManagerData.page.attributes.addCardLocation = location;
        }
        yield put(analyticsActions.productDetailAddToBasket(analyticsAdobeTagManagerData, additionalProducts));
    }
    catch (_b) { }
}
export default function* basketSaga() {
    yield all([
        addProductToBasketSaga(),
        closeBasketPopupSaga(),
        customEventSaga(),
        getBasketSaga(),
        getBasicBasketSaga(),
        getBasketAnalyticsSaga(),
        getBundleBasketAnalyticsSaga(),
        showBasketPopupSaga(),
        showSizeForgottenTooltipSaga(),
        updateBasketQuantitySaga(),
        removeProductFromBasketSaga(),
        removeProductFromBasketAnalyticsSaga(),
        removeProductAndUpdateSaga(),
        resetBasketSaga(),
        checkStockReservationSaga(),
        updateProductQuantitySaga(),
        updateBasketProductAnalyticsSaga(),
        increaseProductQuantitySaga(),
        decreaseProductQuantitySaga(),
        changeSizeSaga(),
        basketOverviewAnalyticsSaga(),
        mergeBasketsSaga(),
        basketPopupClose(),
        cartUpdateTrackingSaga(),
    ]);
}
